import SubmissionPdfDoc from "./submissionPdfDoc";
import T from "./submissionPdfTokens";
import {
  EMPTY,
  dateOnly,
  dateShort,
  dateTime,
  dateTimeDay,
  daysUntil,
  ficoBand,
  fileSize,
  isBlank,
  money,
  percent,
  phone,
  propertyType,
  ssn,
  text,
} from "./submissionPdfFormat";
import {
  LANES,
  LANE_META,
  submissionBorrower,
  submissionEntity,
  submissionBorrowerName,
  submissionDocuments,
  submissionEmail,
  submissionPhone,
} from "./submissionHelpers";
import { AGREEMENT_PARAGRAPHS } from "../LoanApplicationFormScreen/applicationFormShared";
import { CREDIT_AUTH_PARAGRAPHS } from "../CreditAuthorizationFormScreen/creditAuthShared";
import loadSubmissionPdfLogo from "./submissionPdfLogo";

/**
 * One submission → a PDF, laid out per the loan-application design spec.
 *
 * §2.2, the split is semantic rather than overflow-driven:
 *   Page 1 — THE DEAL:   document header → metrics → borrower → loan terms →
 *                        subject property + economics
 *   Page 2 — THE RECORD: continuation header → documents
 *   Page 3 — THE CONSENT: the authorization agreement the applicant executed,
 *                        over their signature — the old portal's "Disclosure &
 *                        Signature" page — then the submission details as its
 *                        footer. Prose pages flow, so a long exit strategy or
 *                        experience answer can push these later.
 *
 * Built client-side from whatever row it is handed — there is no render-to-PDF
 * route on the backend. What that row contains differs by caller, and the
 * difference is visible in the output:
 *   - the detail panel's Download passes the unredacted submission it fetched
 *     by id, so SSN, DOB and the signature image all print;
 *   - the auto-attach on assign passes the grid row, whose list read projected
 *     those out, so they print as em dashes.
 * The SSN line is masked in both cases (§4.1): a PDF gets mailed and filed.
 */

// §5.1 thresholds. Presentation defaults — swap in the lender's real credit-box
// limits when they exist.
const LTARV_FAVORABLE = 0.7;
const LTARV_WATCH = 0.75;
const LTC_WATCH = 0.9;
const CLOSING_IMMINENT_DAYS = 7;

const DOC_TAGS = [
  { tag: "PDF", ext: ["pdf"] },
  { tag: "JPG", ext: ["jpg", "jpeg", "png", "heic"] },
  { tag: "XLS", ext: ["xlsx", "xls", "csv"] },
];

const tagFor = (name = "") => {
  const ext = String(name).split(".").pop()?.toLowerCase();
  return DOC_TAGS.find((t) => t.ext.includes(ext))?.tag || "FILE";
};

const ratio = (num, den) =>
  Number.isFinite(num) && Number.isFinite(den) && den > 0 ? num / den : null;

/** §5 — the four derived cells. Null inputs degrade to an em dash, not a zero. */
const buildMetrics = (row, property) => {
  const L = Number(row.baseLoanAmount);
  const P = Number(property?.purchasePrice);
  const C = Number(property?.constructionBudget) || 0;
  const ARV = Number(property?.arv);

  const loanToArv = ratio(L, ARV);
  const totalCost = Number.isFinite(P) ? P + C : null;
  const loanToCost = ratio(L, totalCost);
  const days = daysUntil(row.expectedClosingDate);

  const arvColor =
    loanToArv == null
      ? T.ink
      : loanToArv <= LTARV_FAVORABLE
        ? T.pine
        : loanToArv > LTARV_WATCH
          ? T.amber
          : T.ink;

  return [
    {
      label: "Base loan amount",
      value: money(row.baseLoanAmount, { cents: false }),
      sub: C ? `+ ${money(C, { cents: false })} construction` : null,
    },
    {
      label: "Loan / ARV",
      value: percent(loanToArv),
      sub: Number.isFinite(ARV) ? `ARV ${money(ARV, { cents: false })}` : null,
      color: arvColor,
    },
    {
      label: "Loan-to-cost",
      value: percent(loanToCost),
      sub: totalCost
        ? `Total cost ${money(totalCost, { cents: false })}`
        : null,
      color: loanToCost != null && loanToCost > LTC_WATCH ? T.amber : T.ink,
    },
    {
      label: "Expected closing",
      // The exit strategy used to hang off this cell; it's free prose and ran
      // straight out of the band, so it prints full-width under Loan terms.
      value: dateShort(row.expectedClosingDate),
      sub:
        days == null
          ? null
          : days === 0
            ? "Today"
            : days > 0
              ? `In ${days} day${days === 1 ? "" : "s"}`
              : `${-days} day${days === -1 ? "" : "s"} ago`,
      color:
        days != null && days <= CLOSING_IMMINENT_DAYS && days >= 0
          ? T.amber
          : T.ink,
    },
  ];
};

/** §6 — bars scaled to ARV, ordered exposure → cost basis → total basis → cushion. */
const buildEconomics = (row, property) => {
  const L = Number(row.baseLoanAmount);
  const P = Number(property?.purchasePrice);
  const C = Number(property?.constructionBudget) || 0;
  const ARV = Number(property?.arv);
  if (!Number.isFinite(ARV) || ARV <= 0) return null;

  const totalCost = Number.isFinite(P) ? P + C : null;
  const equity = totalCost != null ? ARV - totalCost : null;

  return [
    {
      label: "Base loan",
      fraction: ratio(L, ARV),
      color: T.pine,
      value: percent(ratio(L, ARV)),
    },
    {
      label: "Purchase price",
      fraction: ratio(P, ARV),
      color: T.pineMuted,
      value: percent(ratio(P, ARV)),
    },
    {
      label: "Total cost",
      fraction: ratio(totalCost, ARV),
      color: T.pineMuted,
      value: percent(ratio(totalCost, ARV)),
    },
    {
      // The only bar labelled in dollars — it answers "what's the cushion?",
      // and a dollar figure is the honest answer.
      label: "Projected equity",
      fraction: ratio(equity, ARV),
      color: T.gold,
      value: equity != null ? money(equity, { cents: false }) : EMPTY,
      valueColor: T.gold,
    },
  ];
};

/**
 * What the signature line can honestly claim.
 *
 * Only says something when there is NO signature image — the list projection
 * drops it, and migrated legacy submissions never captured one — because the
 * typed name that stands in for it must not read as a captured signature. A
 * record that carries its image needs no caption.
 */
const signatureNote = (row) =>
  row.signature
    ? null
    : "Executed electronically. The name above is the attested signer of record for this submission.";

const fileNameFor = (row, label) =>
  `${[
    label,
    submissionBorrowerName(row) || "Submission",
    (row.submission_date || "").slice(0, 10),
  ]
    .filter(Boolean)
    .join(" - ")
    .replace(/[\\/:*?"<>|]/g, "")}.pdf`;

/**
 * Render a submission and hand back the document plus the filename it should
 * carry. Split out from `downloadSubmissionPdf` so the same bytes can either go
 * to the user's disk or be uploaded to the loan — the file attached to a loan
 * must be the file the user can download, not a second rendering of it.
 */
const buildSubmissionPdf = (row, { logo } = {}) => {
  if (!row) return null;

  const meta = LANE_META[row.__type] || {};
  const label = meta.label || "Submission";
  const isApplication = row.__type === LANES.LOAN_APPLICATION;
  // Both read the merged shape: live submissions arrive with `borrowers` /
  // `entities` empty and the fields inline at the top level, so reading the
  // arrays alone printed a page of em dashes. See submissionBorrower.
  const borrower = submissionBorrower(row);
  // Merged, not read off one side: the entity fields arrive either inline or
  // inside entities[0], and the old fallback only carried the NAME across — so a
  // submission with a top-level entity printed Type / State / EIN as em dashes.
  const entityMerged = submissionEntity(row);
  const entity = entityMerged.entityName ? entityMerged : undefined;
  const properties = Array.isArray(row.subjectProperties)
    ? row.subjectProperties
    : [];
  // `row.documents` is a list of id STRINGS; the names and types are in
  // `uploadMetadata`.
  const documents = submissionDocuments(row);
  const property = properties[0];
  const name = submissionBorrowerName(row) || "Unnamed borrower";

  const doc = new SubmissionPdfDoc({
    identity: [
      label,
      row.loanNumber != null ? `#${row.loanNumber}` : null,
      name,
    ]
      .filter(Boolean)
      .join("  ·  "),
  });
  doc.continuation = { label, refNo: row.loanNumber, borrower: name };

  // ── Page 1: the deal ────────────────────────────────────────────────────
  doc.docHeader({
    eyebrow: label,
    title: name,
    subtitle: [
      row.loanProductType,
      row.loanPurpose,
      property?.address?.fullAddress,
    ]
      .filter(Boolean)
      .join("  ·  "),
    refNo: row.loanNumber,
    logo,
  });

  // Credit authorizations carry no loan economics, so the band and the bars are
  // an application-only surface rather than four empty cells.
  if (isApplication) doc.metricsBand(buildMetrics(row, property));

  doc.section("Borrower");
  doc.fieldColumns(
    [
      { label: "Name", value: text(name) },
      { label: "Email", value: text(submissionEmail(row)) },
      { label: "Phone", value: phone(submissionPhone(row)), mono: true },
      {
        label: "Primary address",
        value: text(
          row.primaryAddress?.fullAddress ||
            borrower.primaryAddress?.fullAddress,
        ),
      },
    ],
    [
      { label: "Citizenship", value: text(borrower.citizenshipStatus) },
      {
        label: "Date of birth",
        value: dateOnly(borrower.dateOfBirth),
        mono: true,
      },
      { label: "Marital status", value: text(borrower.maritalStatus) },
      { label: "SSN", value: ssn(borrower.borrowerSSN), mono: true },
      {
        label: "Estimated FICO",
        value: ficoBand(borrower.estimatedFICO),
        mono: true,
      },
    ],
  );

  if (entity?.entityName) {
    doc.section("Entity");
    doc.fieldColumns(
      [
        { label: "Name", value: text(entity.entityName) },
        { label: "Type", value: text(entity.entityType) },
      ],
      [
        {
          label: "State of organization",
          value: text(entity.entityStateOfOrganization),
        },
        { label: "EIN", value: text(entity.entityEINNumber), mono: true },
      ],
    );
  }

  if (isApplication) {
    doc.section("Loan terms");
    doc.fieldColumns(
      [
        { label: "Purpose", value: text(row.loanPurpose) },
        { label: "Product", value: text(row.loanProductType) },
      ],
      [
        {
          label: "Base loan amount",
          value: money(row.baseLoanAmount),
          mono: true,
        },
        {
          label: "Expected closing",
          value: dateOnly(row.expectedClosingDate),
          mono: true,
        },
      ],
    );

    // Free prose, full width. In a packed column these wrapped into dozens of
    // lines and overran the page.
    if (!isBlank(row.exitStrategy)) {
      doc.blockField({ label: "Exit strategy", value: row.exitStrategy });
    }
    if (!isBlank(row.borrowerExperienceSummary)) {
      doc.blockField({
        label: "Experience",
        value: row.borrowerExperienceSummary,
      });
    }
  }
  // No Credit pull section: the pull happens after this document is signed, so
  // on the authorization itself those four fields are always empty.

  // Applications only. A credit authorization is about a person, not a deal —
  // it carries no property, so the section could only ever print "no subject
  // property on this submission", which says nothing about the authorization.
  if (isApplication && properties.length) {
    properties.forEach((p, i) => {
      doc.section("Subject property", `${i + 1} of ${properties.length}`);
      doc.fieldColumns(
        [
          { label: "Address", value: text(p.address?.fullAddress) },
          { label: "Type", value: propertyType(p.propertyType) },
          {
            label: "Purchase price",
            value: money(p.purchasePrice),
            mono: true,
          },
        ],
        [
          { label: "As-is value", value: money(p.asIsValue), mono: true },
          { label: "After-repair value", value: money(p.arv), mono: true },
          {
            label: "Construction budget",
            value: money(p.constructionBudget),
            mono: true,
          },
        ],
      );

      const bars = buildEconomics(row, p);
      if (bars) doc.economics(bars);
    });
  } else if (isApplication) {
    doc.section("Subject property");
    doc.note("No subject property on this submission.");
  }

  // Documents follow the section above them inline — no page break. Forcing
  // one here bought nothing but blank paper: a short documents list left most
  // of its own page empty and pushed the whole document a page longer. The
  // agreement below is the only deliberate break, because it has to read as a
  // document in its own right.
  doc.section("Documents", documents.length ? `(${documents.length})` : null);
  if (documents.length) {
    doc.documentsTable(
      documents.map((d) => {
        const fileName =
          d.file_display_name || d.name || d.file_storage_name || "Document";
        return {
          name: fileName,
          tag: tagFor(fileName),
          size: fileSize(d.size),
        };
      }),
    );
  } else {
    doc.note("No documents uploaded.");
  }

  // ── Page 3: the executed agreement ──────────────────────────────────────
  // Its own page, as in the old portal's "Disclosure & Signature" page: the
  // consent the borrower signed has to be legible as a document in its own
  // right, not a footnote under the documents table. It runs BEFORE the
  // submission details so the signature that closes it sits directly under the
  // agreement it executes.
  doc.break(doc.continuation);
  doc.section("Authorization agreement");
  doc.paragraphs(isApplication ? AGREEMENT_PARAGRAPHS : CREDIT_AUTH_PARAGRAPHS);

  const signedName =
    text(row.attestation?.signed_name) !== EMPTY
      ? row.attestation.signed_name
      : name;

  doc.signatureBlock({
    label: "Borrower Signature:",
    // Present only on a full record — the list projection drops `signature`,
    // and migrated legacy submissions never captured an image at all.
    signature: row.signature,
    name: signedName,
    date: dateTimeDay(row.attestation?.signed_at || row.submission_date),
    note: signatureNote(row),
  });

  // The record of the submission itself, last: it's the metadata about the
  // signed document above, so it reads as a footer to it.
  doc.section("Submission & signature");
  doc.fieldColumns(
    [
      { label: "Status", value: text(row.status) },
      { label: "Submitted", value: dateTime(row.submission_date), mono: true },
      { label: "Submitted by", value: text(row.submitted_by_email) },
    ],
    [
      { label: "Loan number", value: text(row.loanNumber), mono: true },
      { label: "Signed by", value: text(row.attestation?.signed_name) },
      {
        label: "Signed at",
        value: dateTime(row.attestation?.signed_at),
        mono: true,
      },
      // Two fields are deliberately NOT printed here: the originating IP (an
      // audit-log detail, and a PDF gets mailed and filed) and the consent
      // provenance (`migrated_legacy` etc. — an internal marker; where it
      // matters, the signature note above says so in words).
    ],
  );

  doc.stampFooters(dateTime(new Date().toISOString()));
  return { doc, fileName: fileNameFor(row, label) };
};

/**
 * Resolve the letterhead for one render.
 *
 * Async because the logo is an asset URL and jsPDF needs the bytes; the entry
 * points below are therefore async too. An explicit `logo` in the options wins
 * — including `logo: null`, which is how a caller asks for no letterhead.
 */
const resolveLogo = async (options) => {
  if (options && "logo" in options) return options.logo;
  return loadSubmissionPdfLogo();
};

export const downloadSubmissionPdf = async (row, options) => {
  const built = buildSubmissionPdf(row, { logo: await resolveLogo(options) });
  if (built) built.doc.save(built.fileName);
};

/** The same bytes, for previewing inline via an object URL. */
export const submissionPdfBlob = async (row, options) => {
  const built = buildSubmissionPdf(row, { logo: await resolveLogo(options) });
  return built ? built.doc.blob() : null;
};

/**
 * The submission's PDF as an upload payload for
 * useLoanFilesHook().uploadLoanDocsWithAssociations — the `{ file, id, name,
 * docGroup, docType }` shape useAxiosHook expects (it pulls `.file` off each
 * entry, posts it as `uploadFiles`, and serializes the rest as `submission`).
 *
 * `docType` uses the canonical taxonomy names ("Credit Authorization" / "Loan
 * Application", both under the "Borrower Documents" group), so the attached file
 * lands in the same bucket as one a processor would have uploaded by hand.
 *
 * `id` is what the backend persists as the filename, and it carries a timestamp
 * so re-assigning the same submission doesn't collide with the earlier copy.
 */
export const submissionPdfUploadPayload = async (row, options) => {
  const built = buildSubmissionPdf(row, { logo: await resolveLogo(options) });
  if (!built) return null;

  const { doc, fileName } = built;
  const file = new File([doc.blob()], fileName, { type: "application/pdf" });
  const stamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\..+$/, "");

  return [
    {
      file,
      id: `${fileName.replace(/\.pdf$/i, "")} - ${stamp}.pdf`,
      name: fileName,
      docGroup: "Borrower Documents",
      docType:
        row.__type === LANES.CREDIT_AUTHORIZATION
          ? "Credit Authorization"
          : "Loan Application",
    },
  ];
};

export default downloadSubmissionPdf;
